import { Controller } from "stimulus";
import NotificationChannel from "../channels/notification_channel";

export default class extends Controller {
  static targets = ["bell", "count", "dropdown", "list"];

  connect() {
    console.log("🔔 NotificationBellController connected");

    // Load initial unread count
    this.loadUnreadCount();

    // Subscribe to ActionCable notifications
    this.subscribeToNotifications();

    // Setup dropdown handlers
    this.setupDropdownHandlers();

    // Setup sidebar open/close buttons
    this.setupSidebarHandlers();
  }

  disconnect() {
    if (this.notificationChannel) this.notificationChannel.unsubscribe();
    document.removeEventListener("click", this.boundHideDropdown);
  }

  // ---------------- ActionCable ----------------
  subscribeToNotifications() {
    if (!NotificationChannel) return;

    this.notificationChannel = NotificationChannel.subscribe((data) =>
      this.handleIncomingNotification(data)
    );
  }

  handleIncomingNotification(data) {
    if (!data) return;

    // Update bell count
    this.updateUnreadCount(data.unread_count);

    // Add new notification to dropdown
    if (data.notification) this.addNewNotification(data.notification);

    // Refresh sidebar notifications in real-time
    this.refreshSidebarNotifications();
  }

  // ---------------- Unread count ----------------
  loadUnreadCount() {
    const accountId = this.getAccountId();
    const url = accountId ? `/${accountId}/notifications/unread_count` : "/notifications/unread_count";

    fetch(url, {
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
      },
    })
      .then((r) => r.json())
      .then((data) => this.updateUnreadCount(data.unread_count || 0))
      .catch((e) => console.error("🔔 Error loading unread count:", e));
  }

  updateUnreadCount(count) {
    let countElement = this.hasCountTarget ? this.countTarget : document.querySelector(".notification-count");

    if (count > 0) {
      if (countElement) {
        countElement.textContent = count;
        countElement.style.display = "inline-block";
      } else {
        const bellIcon = document.querySelector(".bell-icon");
        if (bellIcon) {
          const newBadge = document.createElement("span");
          newBadge.className = "notification-count";
          newBadge.dataset.target = "notification-bell.count";
          newBadge.textContent = count;
          newBadge.style.display = "inline-block";
          bellIcon.appendChild(newBadge);
        }
      }
    } else if (countElement) {
      countElement.remove();
    }

    // Update "Mark all as read/unread" link dynamically
    this.updateMarkAllLink(count);
  }

  // ---------------- Dropdown ----------------
  setupDropdownHandlers() {
    this.boundHideDropdown = this.hideDropdown.bind(this);
    document.addEventListener("click", this.boundHideDropdown);
  }

  toggleDropdown(event) {
    event.stopPropagation();
    if (this.hasDropdownTarget) {
      const isHidden = this.dropdownTarget.classList.contains("hidden");
      this.dropdownTarget.classList.toggle("hidden");
      if (isHidden) this.refreshNotificationList();
    }
  }

  hideDropdown(event) {
    if (!this.element.contains(event.target) && this.hasDropdownTarget) {
      this.dropdownTarget.classList.add("hidden");
    }
  }

  refreshNotificationList() {
    if (!this.hasDropdownTarget || this.dropdownTarget.classList.contains("hidden")) return;
    const accountId = this.getAccountId();
    const url = accountId ? `/${accountId}/notifications` : `/notifications`;

    fetch(url, {
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
      },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.notifications) this.updateNotificationList(data.notifications);
        this.updateUnreadCount(data.unread_count || 0);
      })
      .catch((e) => console.error("🔔 Error refreshing notifications:", e));
  }

  updateNotificationList(notifications) {
    if (!this.hasListTarget) return;
    this.listTarget.innerHTML = "";

    const noNotifications = this.element.dataset.notificationsNoNotifications

    if (notifications.length === 0) {
      this.listTarget.innerHTML = `<div class="no-notifications">${noNotifications}</div>`;
      return;
    }

    notifications.slice(0, 10).forEach((n) => {
      this.listTarget.appendChild(this.createNotificationElement(n));
    });
  }

  addNewNotification(notification) {
    if (!this.hasListTarget || !notification) return;

    const element = this.createNotificationElement(notification);
    const firstItem = this.listTarget.querySelector(".notification-item");
    if (firstItem) this.listTarget.insertBefore(element, firstItem);
    else this.listTarget.appendChild(element);

    const noNotifications = this.listTarget.querySelector(".no-notifications");
    if (noNotifications) noNotifications.remove();
  }

  createNotificationElement(notification) {
    const el = document.createElement("div");
    el.className = `notification-item ${notification.read ? "" : "unread"}`;
    el.dataset.notificationId = notification.id;

    const accountId = this.getAccountId();
    const url = accountId ? `/${accountId}/notifications/${notification.id}` : `/notifications/${notification.id}`;

    const timeAgo = notification.created_at ? this.formatTimeAgo(notification.created_at) : "Just now";
    const sourceHtml = notification.source ? `<span class="notification-source">in ${notification.source.class_name_humanized}</span>` : "";

    el.innerHTML = `
      <a href="${url}" class="notification-link">
        <div class="notification-content">
          <div class="notification-title">${notification.title}</div>
          <div class="notification-message">${notification.message}</div>
          <div class="notification-meta">
            <span class="notification-time">${timeAgo}</span>
            ${sourceHtml}
          </div>
        </div>
        ${notification.read ? "" : "<div class='unread-indicator'></div>"}
      </a>
    `;
    return el;
  }

  formatTimeAgo(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
    return `${Math.floor(diff / 86400)} day ago`;
  }

  // ---------------- Mark all as read/unread ----------------
  markAllAsRead(event) {
    event.preventDefault();
    const accountId = this.getAccountId();
    const url = accountId ? `/${accountId}/notifications/mark_all_as_read` : "/notifications/mark_all_as_read";

    fetch(url, {
      method: "PATCH",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
      },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          this.updateUnreadCount(0);
          this.markAllReadUI();
          this.markAllReadSidebarUI();
        }
      })
      .catch((e) => console.error("Error marking all as read:", e));
  }

  markAllReadUI() {
    if (!this.hasListTarget) return;
    const unreadItems = this.listTarget.querySelectorAll(".notification-item.unread");
    unreadItems.forEach((item) => {
      item.classList.remove("unread");
      const indicator = item.querySelector(".unread-indicator");
      if (indicator) indicator.remove();
    });
  }

  markAllAsUnread(event) {
    event.preventDefault();
    const accountId = this.getAccountId();
    const url = accountId ? `/${accountId}/notifications/mark_all_as_unread` : "/notifications/mark_all_as_unread";

    fetch(url, {
      method: "PATCH",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
      },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          this.loadUnreadCount();
          this.refreshNotificationList();
          this.refreshSidebarNotifications();
        }
      })
      .catch((e) => console.error("Error marking all as unread:", e));
  }

  // ---------------- Helpers ----------------
  getAccountId() {
    const urlMatch = window.location.pathname.match(/\/(\d+)\//);
    if (urlMatch) return urlMatch[1];
    const el = document.querySelector("[data-account-id]");
    return el ? el.getAttribute("data-account-id") : null;
  }

  updateMarkAllLink(unreadCount) {
    const header = this.dropdownTarget?.querySelector(".notification-header");
    if (!header) return;

    let markAllLink = header.querySelector(".mark-all-read, .mark-all-unread");
    const markAllAsRead = this.element.dataset.notificationsMarkAllAsRead
    const markAllAsUnRead = this.element.dataset.notificationsMarkAllAsUnread

    if (unreadCount > 0) {
      if (!markAllLink || markAllLink.classList.contains("mark-all-unread")) {
        markAllLink?.remove();
        markAllLink = document.createElement("a");
        markAllLink.href = `/${this.getAccountId()}/notifications/mark_all_as_read`;
        markAllLink.className = "mark-all-read";
        markAllLink.dataset.action = "click->notification-bell#markAllAsRead";
        markAllLink.textContent = markAllAsRead;
        header.appendChild(markAllLink);
      }
    } else {
      if (!markAllLink || markAllLink.classList.contains("mark-all-read")) {
        markAllLink?.remove();
        markAllLink = document.createElement("a");
        markAllLink.href = `/${this.getAccountId()}/notifications/mark_all_as_unread`;
        markAllLink.className = "mark-all-unread";
        markAllLink.dataset.action = "click->notification-bell#markAllAsUnread";
        markAllLink.textContent = markAllAsUnRead;
        header.appendChild(markAllLink);
      }
    }
  }

  // ---------------- Sidebar Logic ----------------
  setupSidebarHandlers() {
    const viewAllLinks = document.querySelectorAll(".view-all-link");
    const closeBtn = document.getElementById("closeSidebar");

    viewAllLinks.forEach(link => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        this.openSidebar();
      });
    });

    if (closeBtn) closeBtn.addEventListener("click", () => this.closeSidebar());

    // Close sidebar when clicking outside
    document.addEventListener("click", (e) => {
      const sidebar = document.getElementById("notificationsSidebar");
      if (sidebar && sidebar.classList.contains("open") && !sidebar.contains(e.target) && !e.target.closest(".view-all-link")) {
        this.closeSidebar();
      }
    });
  }

  openSidebar() {
    const sidebar = document.getElementById("notificationsSidebar");
    if (!sidebar) return;

    if (this.hasDropdownTarget) this.dropdownTarget.classList.add("hidden");

    sidebar.classList.add("open");
    this.refreshSidebarNotifications();
  }

  closeSidebar() {
    const sidebar = document.getElementById("notificationsSidebar");
    if (!sidebar) return;
    sidebar.classList.remove("open");
  }

  refreshSidebarNotifications() {
    const accountId = this.getAccountId();
    const url = accountId ? `/${accountId}/notifications` : `/notifications`;

    fetch(url, {
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
      },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.notifications) this.updateSidebarList(data.notifications);
      })
      .catch((e) => console.error("🔔 Error refreshing sidebar notifications:", e));
  }

  updateSidebarList(notifications) {
    const sidebarList = document.querySelector(".notifications-sidebar .notifications-list");
    if (!sidebarList) return;

    const noNotifications = this.element.dataset.notificationsNoNotifications
    const notificationsDescription = this.element.dataset.notificationsNotificationsDescription

    sidebarList.innerHTML = "";

    if (notifications.length === 0) {
      sidebarList.innerHTML = `
        <div class="no-notifications">
          <div class="empty-state">
            <div class="empty-icon">🔔</div>
            <h3>${noNotifications}</h3>
            <p>${notificationsDescription}</p>
          </div>
        </div>
      `;
      return;
    }

    notifications.forEach(notification => {
      sidebarList.appendChild(this.createSidebarNotificationElement(notification));
    });
  }

  createSidebarNotificationElement(notification) {
    const el = document.createElement("div");
    el.className = `notification-item ${notification.read ? "" : "unread"}`;
    el.dataset.notificationId = notification.id;

    const accountId = this.getAccountId();
    const url = accountId ? `/${accountId}/notifications/${notification.id}` : `/notifications/${notification.id}`;

    const timeAgo = notification.created_at ? this.formatTimeAgo(notification.created_at) : "Just now";
    const sourceHtml = notification.source ? `<span class="notification-source">in ${notification.source.class_name_humanized}</span>` : "";

    el.innerHTML = `
      <a href="${url}" class="notification-link">
        <div class="notification-content">
          <div class="notification-title">${notification.title}</div>
          <div class="notification-message">${notification.message}</div>
          <div class="notification-meta">
            <span class="notification-time">${timeAgo}</span>
            ${sourceHtml}
          </div>
        </div>
        ${notification.read ? "" : "<div class='unread-indicator'></div>"}
      </a>
    `;
    return el;
  }

  markAllReadSidebarUI() {
    const sidebarList = document.querySelector(".notifications-sidebar .notifications-list");
    if (!sidebarList) return;

    const unreadItems = sidebarList.querySelectorAll(".notification-item.unread");
    unreadItems.forEach(item => {
      item.classList.remove("unread");
      const indicator = item.querySelector(".unread-indicator");
      if (indicator) indicator.remove();
    });
  }
}
